
import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Calendar,
  Edit,
  Plus,
  ShoppingCart,
  Loader2,
  User as UserIcon
} from 'lucide-react';
import { patientsApi } from '../../../services/patientsService';
import { useSelector } from 'react-redux';
import { selectCurrentClinicId } from '../../../store/slices/authSlice';
import type { Patient } from '../../../types/api';
import type { Api } from '../../../types/generated';

interface PatientDetailModalProps {
  patient: Patient | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onCreateOrder: () => void;
}

const PatientDetailModal: React.FC<PatientDetailModalProps> = ({
  patient,
  isOpen,
  onClose,
  onEdit,
  onCreateOrder
}) => {
  const [allergies, setAllergies] = useState<Api.ClinicPatientAllergySearch200Response.Item[]>([]);
  const [allergiesLoading, setAllergiesLoading] = useState(false);
  const [allergiesError, setAllergiesError] = useState<string | null>(null);
  
  const currentClinicId = useSelector(selectCurrentClinicId);

  // Load allergies when patient or dialog opens
  useEffect(() => {
    if (patient && isOpen && currentClinicId) {
      loadPatientAllergies();
    } else if (!isOpen) {
      // Clear allergies when modal closes to prevent stale state
      setAllergies([]);
      setAllergiesError(null);
    }
  }, [patient, isOpen, currentClinicId]);

  const loadPatientAllergies = async () => {
    console.log(`[PatientDetailModal] loadPatientAllergies called for patient:`, patient?.id);
    if (!patient || !patient.allergies || typeof patient.allergies !== 'object' || !('href' in patient.allergies)) {
      console.log(`[PatientDetailModal] Early return - patient: ${!!patient}, allergies href: ${patient?.allergies && typeof patient.allergies === 'object' && 'href' in patient.allergies ? patient.allergies.href : 'missing'}`);
      return;
    }
    
    console.log(`[PatientDetailModal] Starting allergy load for ${patient.id}`);
    setAllergiesLoading(true);
    setAllergiesError(null);
    
    try {
      const patientAllergies = await patientsApi.getPatientAllergies(patient.allergies.href);
      console.log(`[PatientDetailModal] Got allergies:`, patientAllergies);
      setAllergies(patientAllergies);
    } catch (error) {
      console.error('[PatientDetailModal] Failed to load patient allergies:', error);
      setAllergiesError('Failed to load allergies');
    } finally {
      setAllergiesLoading(false);
      console.log(`[PatientDetailModal] Finished allergy load for ${patient.id}`);
    }
  };

  if (!patient) return null;

  const calculateAge = (dob: string) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };


  const handleCreateOrder = () => {
    onCreateOrder();
  };


  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto border-l-4 border-l-emerald-500 shadow-lg shadow-emerald-100/50">
          <DialogHeader>
            <DialogTitle className="text-2xl font-syne font-medium text-vitl-navy flex items-center space-x-3">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-500 rounded-full flex items-center justify-center text-white font-syne font-medium text-xl">
                {patient.first_name?.[0]?.toUpperCase()}{patient.last_name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div>{patient.first_name} {patient.last_name}</div>
                <p className="text-sm text-vitl-dark/70 font-lato font-normal">
                  {patient.gender}, {patient.date_of_birth ? calculateAge(patient.date_of_birth) : 'Unknown age'} years old
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            {/* Patient Information */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-syne font-medium text-vitl-navy mb-4 border-b border-emerald-100 pb-2">Patient Information</h3>
                <div className="space-y-4">
                  {patient.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-vitl-dark/50" />
                      <div>
                        <p className="text-sm font-lato font-medium text-vitl-dark/60">Phone</p>
                        <p className="text-vitl-dark font-lato">{patient.phone}</p>
                      </div>
                    </div>
                  )}
                  
                  {patient.email && (
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-vitl-dark/50" />
                      <div>
                        <p className="text-sm font-lato font-medium text-vitl-dark/60">Email</p>
                        <p className="text-vitl-dark font-lato">{patient.email}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-vitl-dark/50 mt-1" />
                    <div>
                      <p className="text-sm font-lato font-medium text-vitl-dark/60">Address</p>
                      {patient.address ? (
                        <p className="text-vitl-dark font-lato">
                          {patient.address.street?.join(', ')}
                          <br />
                          {patient.address.city}, {patient.address.state} {patient.address.postal_code}
                        </p>
                      ) : (
                        <p className="text-gray-400 font-lato italic">No address provided</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-vitl-dark/50" />
                    <div>
                      <p className="text-sm font-lato font-medium text-vitl-dark/60">Date of Birth</p>
                      <p className="text-vitl-dark font-lato">{new Date(patient.date_of_birth).toLocaleDateString()}</p>
                    </div>
                  </div>

                  
                </div>
              </div>

              {/* Allergies Section - Loaded Separately */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-sm font-lato font-medium text-red-800 mb-2 flex items-center space-x-2">
                  <span>Known Allergies</span>
                  {allergiesLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                </h4>
                
                {allergiesError ? (
                  <p className="text-red-600 font-lato text-sm">⚠️ {allergiesError}</p>
                ) : allergiesLoading ? (
                  <p className="text-red-700 font-lato">Loading allergies...</p>
                ) : allergies.length > 0 ? (
                  <div className="space-y-1">
                    {allergies.map((allergy, index) => (
                      <p key={allergy.id || index} className="text-red-700 font-lato">
                        • {allergy.name || 'Unknown allergy'}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-red-600 font-lato italic">No known allergies</p>
                )}
              </div>

              <div className="flex space-x-3">
                <Button 
                  onClick={onEdit}
                  variant="outline"
                  className="flex items-center space-x-2 font-lato border-gray-300 hover:border-emerald-400"
                >
                  <Edit className="w-4 h-4" />
                  <span>Edit Patient</span>
                </Button>
                <Button 
                  onClick={handleCreateOrder}
                  className="bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white font-lato flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create Order</span>
                </Button>
              </div>
            </div>

            {/* Order History - Placeholder for future implementation */}
            <div>
              <h3 className="text-lg font-syne font-medium text-vitl-navy mb-4 border-b border-emerald-100 pb-2 flex items-center space-x-2">
                <ShoppingCart className="w-5 h-5" />
                <span>Order History</span>
              </h3>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                <div className="text-center py-8">
                  <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-vitl-dark/50 font-lato">Order history coming soon</p>
                  <p className="text-sm text-vitl-dark/40 font-lato mb-4">
                    Orders will be fetched from prescriptions API
                  </p>
                 
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Type Dialog - TODO: Update to use real API Patient types */}
      {/* <OrderTypeDialog
        open={showOrderTypeDialog}
        onOpenChange={setShowOrderTypeDialog}
        onSelectType={handleOrderTypeSelect}
        origin="dashboard"
        preSelectedPatient={patient}
      /> */}
    </>
  );
};

export default PatientDetailModal;
